import { useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, Send, Smile, X } from 'lucide-react';
import IconButton from '../ui/IconButton';
import EmojiPicker from './EmojiPicker';
import {
  CHAT_IMAGE_ALLOWED_MIMES,
  CHAT_IMAGE_MAX_BYTES,
  COUNTER_WARN_AT,
  MESSAGE_LIMIT,
} from '../../lib/constants';

const MAX_TEXTAREA_DESKTOP = 148;
const MAX_TEXTAREA_MOBILE = 120;
const MAX_IMAGE_MB = Math.round(CHAT_IMAGE_MAX_BYTES / (1024 * 1024));

/**
 * Rejection copy is derived from the same list the picker and the server use, so
 * it can never advertise a format the upload would refuse.
 */
const ALLOWED_TYPE_LABEL = CHAT_IMAGE_ALLOWED_MIMES.map((mime) =>
  mime.slice('image/'.length).toUpperCase(),
).join(', ');

/**
 * One gesture can reach more than one listener: some engines dispatch the same
 * native `paste` twice, and a drop that mirrors a paste carries the very same
 * File objects. Both are deduped so one copied screenshot attaches exactly once.
 * The window is deliberately tiny - a paste and its mirror drop fire in the same
 * instant, while deliberately pasting the same image twice must still work.
 */
const DUPLICATE_FILE_WINDOW_MS = 250;

const fileKey = (file) => `${file.name}|${file.size}|${file.lastModified}`;

/** Keep the grown textarea from eating a phone screen once the keyboard is up. */
const maxTextareaHeight = () =>
  typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
    ? MAX_TEXTAREA_DESKTOP
    : MAX_TEXTAREA_MOBILE;

let attachmentId = 0;

/**
 * Message composer.
 *
 * Attachments are held as File objects with a local data-URL preview; the actual
 * upload happens on send (see useMessages), so nothing is stored until the
 * message that references it exists.
 *
 * Everything that can produce a File - the attach button, a clipboard paste and
 * any future drop target - funnels through `addFiles`, so every source shares
 * one allowlist, one size cap and the same preview/remove UI.
 *
 * Limits come from lib/constants.js, which mirrors the server's schema and multer
 * config, so the client cannot offer something the API will reject.
 */
export default function MessageInput({ onSend, onTyping, disabled = false, sending = false, sendProgress = null }) {
  const [text, setText] = useState('');

  // "Moderator tools" in the header drops a prepared reply here; the user can
  // still edit it before sending.
  useEffect(() => {
    const onInsert = (event) => {
      const value = event?.detail;
      if (typeof value !== 'string' || !value.trim()) return;
      setText((prev) => (prev.trim() ? prev.replace(/\s+$/, '') + ' ' + value : value));
    };
    window.addEventListener('z2m:insert-message', onInsert);
    return () => window.removeEventListener('z2m:insert-message', onInsert);
  }, []);
  const [attachments, setAttachments] = useState([]);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [error, setError] = useState('');

  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  // One gesture, one attach: see DUPLICATE_FILE_WINDOW_MS.
  const lastPasteEventRef = useRef(null);
  const recentFilesRef = useRef(new Map());
  // Throttled "still typing" ping: the partner only needs a heartbeat, not one
  // frame per keystroke, and the server fans every ping out over the socket.
  const typingPingRef = useRef({ at: 0, active: false });

  useEffect(() => {
    const element = textareaRef.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, maxTextareaHeight())}px`;
  }, [text]);

  const canSend =
    !disabled && !sending && (text.trim().length > 0 || attachments.length > 0);

  /** Tell the partner we are typing (at most one ping every 1.5s), or that we stopped. */
  const signalTyping = (active) => {
    if (!onTyping) return;
    const now = Date.now();
    const state = typingPingRef.current;

    if (active) {
      if (state.active && now - state.at < 1500) return;
      typingPingRef.current = { at: now, active: true };
      onTyping(true);
      return;
    }

    if (!state.active) return;
    typingPingRef.current = { at: now, active: false };
    onTyping(false);
  };

  // A tab closed mid-word must not leave "typing…" stuck on the partner.
  useEffect(() => () => signalTyping(false), []);

  /** Insert at the caret the way a native paste would, leaving the caret after it. */
  const insertText = (snippet) => {
    const element = textareaRef.current;
    if (!element) {
      setText((current) => (current + snippet).slice(0, MESSAGE_LIMIT));
      return;
    }

    const start = element.selectionStart ?? text.length;
    const end = element.selectionEnd ?? text.length;
    const next = (text.slice(0, start) + snippet + text.slice(end)).slice(0, MESSAGE_LIMIT);

    setText(next);

    requestAnimationFrame(() => {
      element.focus();
      const caret = Math.min(start + snippet.length, MESSAGE_LIMIT);
      element.setSelectionRange(caret, caret);
    });
  };

  const insertEmoji = (emoji) => insertText(emoji);

  /** Says exactly which file was refused, why, and what would have been accepted. */
  const describeRejections = (rejected) => {
    const wrongType = rejected.filter((file) => !CHAT_IMAGE_ALLOWED_MIMES.includes(file.type));
    const tooLarge = rejected.filter((file) => file.size > CHAT_IMAGE_MAX_BYTES);
    const clauses = [];

    if (wrongType.length > 0) {
      clauses.push(
        wrongType.length === 1
          ? `"${wrongType[0].name || 'That file'}" (${wrongType[0].type || 'unknown type'}) is not an allowed file type.`
          : `${wrongType.length} files are not an allowed file type.`,
      );
    }

    if (tooLarge.length > 0) {
      clauses.push(
        tooLarge.length === 1
          ? `"${tooLarge[0].name || 'That image'}" is ${(
              tooLarge[0].size /
              (1024 * 1024)
            ).toFixed(1)} MB, over the limit.`
          : `${tooLarge.length} images are over the limit.`,
      );
    }

    return `${clauses.join(' ')} Allowed: ${ALLOWED_TYPE_LABEL} images up to ${MAX_IMAGE_MB} MB.`;
  };

  /**
   * The single entry point for every attachment source. The file picker, a
   * clipboard paste and any future drag-and-drop all land here, so they obey the
   * same allowlist, the same size cap and the same preview/remove affordance.
   */
  const addFiles = (incoming) => {
    const files = Array.from(incoming ?? []).filter(Boolean);
    if (files.length === 0) return;

    const allowed = (file) =>
      CHAT_IMAGE_ALLOWED_MIMES.includes(file.type) && file.size <= CHAT_IMAGE_MAX_BYTES;

    const rejected = files.filter((file) => !allowed(file));
    const accepted = files.filter(allowed);

    setError(rejected.length > 0 ? describeRejections(rejected) : '');

    accepted.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setAttachments((current) => [
          ...current,
          {
            id: `att-${(attachmentId += 1)}`,
            name: file.name || 'Pasted image',
            file,
            dataUrl: String(reader.result),
          },
        ]);
      };
      reader.onerror = () => setError('That image could not be read.');
      reader.readAsDataURL(file);
    });
  };

  const handleFilesPicked = (event) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    addFiles(files);
  };

  /** Drops files an identical paste/drop already attached moments ago. */
  const takeFreshFiles = (files) => {
    const now = Date.now();

    for (const [key, seenAt] of recentFilesRef.current) {
      if (now - seenAt > DUPLICATE_FILE_WINDOW_MS) recentFilesRef.current.delete(key);
    }

    return files.filter((file) => {
      const key = fileKey(file);
      if (recentFilesRef.current.has(key)) return false;
      recentFilesRef.current.set(key, now);
      return true;
    });
  };

  /**
   * Clipboard -> attachment.
   *
   * A copied screenshot (or any copied file) arrives on `clipboardData.files`;
   * Chromium also lists it as an item of `kind: 'file'`, which is the fallback
   * for engines that only fill `items`. A plain text or rich-text paste has
   * neither, so it returns early and the default paste is never touched.
   *
   * `preventDefault` only happens once real files are in hand - it stops the
   * browser dropping the copied markup or image URL into the composer next to
   * the thumbnail. Text that travelled with those files is re-inserted by hand,
   * so a "screenshot + text" paste keeps both halves.
   */
  const handlePaste = (event) => {
    const clipboard = event.clipboardData;
    if (!clipboard) return;

    // The same native event reaching this handler twice must not attach twice.
    const native = event.nativeEvent ?? event;
    if (lastPasteEventRef.current === native) return;
    lastPasteEventRef.current = native;

    const listed = Array.from(clipboard.files ?? []);
    const files =
      listed.length > 0
        ? listed
        : Array.from(clipboard.items ?? [])
            .filter((item) => item.kind === 'file')
            .map((item) => item.getAsFile())
            .filter(Boolean);

    if (files.length === 0) return; // typing / text paste: browser default stands

    event.preventDefault();

    // Mirrors the attach button, which is inert while a message is in flight.
    if (disabled || sending) {
      setError('Wait for the current message to finish sending, then paste again.');
      return;
    }

    const textAlong = clipboard.getData('text/plain');
    if (textAlong) insertText(textAlong);

    addFiles(takeFreshFiles(files));
  };

  const removeAttachment = (id) => {
    setAttachments((current) => current.filter((item) => item.id !== id));
    setError('');
  };

  /**
   * Sending several attachments is several messages, and the server takes one
   * per second (see useMessages). The hook reports how far through the queue it
   * is so the wait is visible rather than looking like a hung composer.
   */
  const queueLabel =
    sendProgress && sendProgress.total > 1
      ? `Sending ${sendProgress.current} of ${sendProgress.total}…`
      : '';

  const submit = async () => {
    if (!canSend) return;

    const draftText = text;
    const payload = { text, files: attachments.map((item) => item.file) };

    try {
      const result = await onSend(payload);

      // A refused duplicate (a send already in flight) leaves the draft alone.
      if (result?.skipped) return;

      if (!result || result.ok) {
        // Only clear once the server has accepted it, so a failed send keeps the draft.
        signalTyping(false);
        setText('');
        setAttachments([]);
        setEmojiOpen(false);
        setError('');
        textareaRef.current?.focus();
        return;
      }

      // Partial or total failure: whatever did not reach the server stays in the
      // composer - only the attachments that actually went out are cleared - and
      // the reason is spelled out instead of a raw status code.
      const failedFiles = new Set(result.failedFiles ?? []);
      setAttachments((current) => current.filter((item) => failedFiles.has(item.file)));
      setText(result.failedText ? draftText : '');
      setError(result.error?.message || 'Message could not be sent. Please try again.');
      textareaRef.current?.focus();
    } catch (sendError) {
      setError(sendError?.message || 'Message could not be sent.');
    }
  };

  const handleKeyDown = (event) => {
    if (event.nativeEvent?.isComposing) return;

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }

    if (event.key === 'Escape' && emojiOpen) setEmojiOpen(false);
  };

  const counterTone =
    text.length >= MESSAGE_LIMIT
      ? 'text-rose-500'
      : text.length >= COUNTER_WARN_AT
        ? 'text-amber-500'
        : 'text-ink-3';

  return (
    // `shrink-0` + `sticky bottom-0` is what keeps this bar on screen: the shell
    // is exactly as tall as the visible viewport, the message list is the only
    // thing that flexes, and the safe-area padding keeps the controls clear of
    // the home indicator. Sticky is belt-and-braces for any case where the
    // column is still taller than the shell.
    <div
      onPaste={handlePaste}
      className="sticky bottom-0 z-10 shrink-0 border-t border-line bg-surface px-2.5 pt-2 pb-[max(0.625rem,env(safe-area-inset-bottom))] sm:px-4 sm:pt-3 sm:pb-[max(0.875rem,env(safe-area-inset-bottom))]"
    >
      {error && (
        <p role="alert" className="mb-2 text-[11.5px] font-medium text-rose-500">
          {error}
        </p>
      )}

      {queueLabel && (
        <p role="status" aria-live="polite" className="mb-2 text-[11.5px] font-medium text-ink-2">
          {queueLabel}
        </p>
      )}

      {attachments.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-2">
          {attachments.map((item) => (
            <li key={item.id} className="animate-z2-rise relative">
              <img
                src={item.dataUrl}
                alt={item.name}
                title={item.name}
                className="h-14 w-14 rounded-xl border border-line object-cover sm:h-16 sm:w-16"
              />
              <button
                type="button"
                onClick={() => removeAttachment(item.id)}
                aria-label={`Remove ${item.name}`}
                className="absolute -top-1.5 -right-1.5 grid h-5 w-5 place-items-center rounded-full bg-ink text-surface shadow-soft transition hover:bg-rose-500"
              >
                <X size={11} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="relative flex items-end gap-1 rounded-2xl border border-line bg-sunken p-1.5 transition focus-within:border-brand-500 focus-within:ring-4 focus-within:ring-brand-500/10">
        <IconButton
          label="Attach an image"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || sending}
          className="mb-0.5"
        >
          <ImagePlus size={17} />
        </IconButton>

        <IconButton
          label="Insert emoji"
          active={emojiOpen}
          onClick={() => setEmojiOpen((open) => !open)}
          disabled={sending}
          className="mb-0.5"
        >
          <Smile size={17} />
        </IconButton>

        <input
          ref={fileInputRef}
          type="file"
          accept={CHAT_IMAGE_ALLOWED_MIMES.join(',')}
          multiple
          hidden
          onChange={handleFilesPicked}
        />

        {/* 16px on phones: anything smaller makes iOS Safari zoom the page on
            focus, which is exactly how the composer ends up off screen. */}
        <textarea
          ref={textareaRef}
          rows={1}
          value={text}
          maxLength={MESSAGE_LIMIT}
          disabled={disabled}
          onChange={(event) => {
            const next = event.target.value;
            setText(next);
            signalTyping(next.trim().length > 0);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => signalTyping(false)}
          placeholder="Type your message here..."
          aria-label="Message"
          enterKeyHint="send"
          autoComplete="off"
          className="z2-scroll max-h-[120px] min-h-[36px] flex-1 resize-none bg-transparent px-1.5 py-2 text-[16px] leading-snug text-ink outline-none placeholder:text-ink-3 disabled:cursor-not-allowed lg:max-h-[148px] lg:text-[13px] lg:leading-relaxed"
        />

        <div className="mb-0.5 flex shrink-0 items-center gap-1.5 sm:gap-2">
          {/* On a phone the counter only appears once it matters, which buys the
              textarea ~40px of width; from sm up it is always visible. */}
          <span
            className={`${
              text.length >= COUNTER_WARN_AT ? 'inline' : 'hidden sm:inline'
            } text-[10.5px] font-medium tabular-nums ${counterTone}`}
            aria-live="polite"
          >
            {`${text.length}/${MESSAGE_LIMIT}`}
          </span>

          <button
            type="button"
            onClick={submit}
            disabled={!canSend}
            aria-label={sending ? 'Sending message' : 'Send message'}
            className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl transition lg:h-9 lg:w-9 ${
              canSend
                ? 'bg-brand-600 text-white hover:bg-brand-700 hover:shadow-glow active:scale-95'
                : 'cursor-not-allowed bg-surface-3 text-ink-3'
            }`}
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>

        <EmojiPicker open={emojiOpen} onPick={insertEmoji} onClose={() => setEmojiOpen(false)} />
      </div>

      {/* Keyboard hint only where there is a physical keyboard - on a phone the
          return key itself is labelled "send" (enterKeyHint above). */}
      <p className="mt-1.5 hidden px-1 text-[10.5px] text-ink-3 md:block">
        Enter to send · Shift + Enter for a new line · Paste a screenshot or image to attach it
      </p>
    </div>
  );
}
